<script lang="ts">
    import { createEventDispatcher, onMount } from 'svelte/internal'
    const dispatch = createEventDispatcher()
    import { faAngleLeft, faEdit, faPlusCircle, faSave, faTrashAlt } from '@fortawesome/free-solid-svg-icons'
    import Content from '../_common/content.svelte'
    import LoadSpinner from '../_common/load-spinner.svelte'
    import { PaymentLink } from '../../modules/paymentsApi/payment-links/payment-link.entity'
    import type { CreatePaymentLinkDto } from '../../modules/paymentsApi/payment-links/dto/create-payment-link.dto'
    import type { Gateway } from '../../modules/paymentsApi/gateways/gateway.entity'
    import { PurchaseType, PurchaseTypeFriendly, isSubscriptionType } from '../../modules/paymentsApi/purchases/purchase.enum'
    import { UpdatePaymentLinkDto } from '../../modules/paymentsApi/payment-links/dto/update-payment-link.dto'
    import { GridButton, GridClass, GridCol, GridColTypeEnum, GridLine } from '../_common/z/zgrid'
    import Zgrid from '../_common/z/zgrid.svelte'
    import Zframe from '../_common/z/zframe.svelte'
    import Zact from '../_common/z/zact.svelte'
    import Zbutton from '../_common/z/zbutton.svelte'
    import Zinput from '../_common/z/zinput.svelte'
    import ZContainer from '../_common/z/z-container.svelte'
    import ZContainerCol from '../_common/z/z-container-col.svelte'
    import ZentitySelect from '../_common/z/zentity-select.svelte'
    import { Service } from '../../modules/posApi/services/service'
    import { User } from '../../modules/usersApi/users/user'
    import { PaymentMethod } from '../../modules/posApi/payment-methods/payment-methods'
    import { PaymentMethods, PaymentMethodsFriendly } from '../../modules/paymentsApi/charges/charge.enum'
    import ZinputTitle from '../_common/z/zinput-title.svelte'
    import ZinputBreak from '../_common/z/zinput-break.svelte'
    // ##############################################################################################################

    export let form: boolean = false
    export let paymentLinkId: string = null
    let entity: PaymentLink = new PaymentLink()

    let purchaseTotalLabel = 'Valor total'

    let selectedService: Service = null
    let selectedUser: User = null
    let selectedPayment: PaymentMethod = null

    $: {
        if (entity?.purchaseType === PurchaseType.recurring_continuous) {
            purchaseTotalLabel = 'Valor mensal'
        } else {
            purchaseTotalLabel = 'Valor total'
        }
    }

    async function editEntity(id: string) {
        try {
            show = true
            entity = new PaymentLink()
            entity = await window.api.payments.paymentLinks.getOne(id)
            selectedPayment = new PaymentMethod()
            selectedPayment.paymentMethodId = entity.purchasePaymentId
            selectedUser = new User()
            selectedUser.userId = entity.purchaseUserId
            selectedService = new Service()
            selectedService.serviceId = entity.purchaseServiceId
            console.log('payment-link', entity)
            step = 2
        } catch (error) {
            window.error(error)
        } finally {
            show = false
        }
    }

    function newEntity() {
        entity = new PaymentLink()
        entity.purchaseLaunchType = 'every'
        entity.purchaseType = PurchaseType.recurring_continuous
        step = 2
    }

    function evaluateDto(dto: Partial<PaymentLink>) {
        if (!dto.gatewayId) {
            window.error('Informe o gateway de pagamento')
            return false
        }
        if (!dto.purchaseDescription) {
            window.error('Informe a descrição da compra')
            return false
        }
        if (!dto.purchaseType) {
            window.error('Informe o método de cobrança')
            return false
        }
        if (dto.purchaseType !== PurchaseType.recurring_continuous && !dto.purchaseTotalInstallments) {
            window.error('Informe a quantidade máxima de parcelas permitida para este link')
            return false
        }
        if (!dto.purchaseQuantity) {
            window.error('Informe a quantidade de itens a serem adquiridos com este link')
            return false
        }
        if (!dto.purchaseTotalValue) {
            window.error('Informe o valor a ser cobrado com este link')
            return false
        }
        if (!validAutoCancelDays(dto.autoCancelOverdueDays)) return false
        return true
    }

    function validAutoCancelDays(days: number) {
        if (days === null || days === undefined) return true
        if (!Number.isInteger(days) || days < 1) {
            window.error('Os dias de atraso para cancelar precisam ser um número inteiro maior que zero')
            return false
        }
        return true
    }

    // cancelar por atraso usa o cancelamento agendado, que só existe no recorrente contínuo.
    // campo vazio = nunca cancela por atraso.
    function autoCancelDays(item: PaymentLink): number {
        if (item.purchaseType !== PurchaseType.recurring_continuous) return null
        const days: any = item.autoCancelOverdueDays
        return days === null || days === undefined || days === '' ? null : Number(days)
    }

    async function createEntity() {
        try {
            show = true
            entity.purchaseUserId = selectedUser.userId
            entity.purchasePaymentId = selectedPayment?.paymentMethodId
            entity.purchaseServiceId = selectedService.serviceId
            let dto: CreatePaymentLinkDto = {
                gatewayId: entity.gatewayId,
                paymentMethodsAllowed: entity.paymentMethodsAllowed,
                purchaseDescription: entity.purchaseDescription,
                purchaseQuantity: 1,
                purchaseTotalInstallments: entity.purchaseTotalInstallments,
                purchaseTotalValue: entity.purchaseTotalValue,
                purchaseType: entity.purchaseType,
                referenceId: null,
                category: 'subscription',
                purchasePaymentId: entity.purchasePaymentId,
                purchaseUserId: entity.purchaseUserId,
                purchaseLaunchType: entity.purchaseLaunchType,
                purchaseServiceId: entity.purchaseServiceId,
                purchaseInterval: entity.purchaseInterval,
                autoCancelOverdueDays: autoCancelDays(entity),
            }
            if (entity.purchaseType === PurchaseType.recurring_continuous) {
                dto.purchaseTotalInstallments = 1
            }
            if (!evaluateDto(dto)) return
            entity = await window.api.payments.paymentLinks.create(dto)
            window.api.users.statistics.tick('pay.subscription.create')
            if (form) {
                sendClose()
                return
            }
            await loadEntities()
        } catch (error) {
            window.error(error)
        } finally {
            show = false
        }
    }

    function sendClose() {
        dispatch('close')
    }

    async function updateEntity() {
        try {
            show = true
            entity.purchaseUserId = selectedUser.userId
            entity.purchasePaymentId = selectedPayment.paymentMethodId
            entity.purchaseServiceId = selectedService.serviceId
            let dto: UpdatePaymentLinkDto = {
                gatewayId: entity.gatewayId,
                paymentMethodsAllowed: entity.paymentMethodsAllowed,
                purchaseDescription: entity.purchaseDescription,
                purchaseQuantity: 1, // entity.purchaseQuantity,
                purchaseTotalInstallments: entity.purchaseTotalInstallments,
                purchaseTotalValue: entity.purchaseTotalValue,
                purchaseType: entity.purchaseType,
                category: 'subscription',
                purchaseLaunchType: entity.purchaseLaunchType,
                purchaseServiceId: entity.purchaseServiceId,
                purchasePaymentId: entity.purchasePaymentId,
                purchaseUserId: entity.purchaseUserId,
                purchaseInterval: entity.purchaseInterval,
                autoCancelOverdueDays: autoCancelDays(entity),
            }
            if (!validAutoCancelDays(dto.autoCancelOverdueDays)) return
            await window.api.payments.paymentLinks.update(entity.paymentLinkId, dto)
            if (form) {
                sendClose()
                return
            }
            await loadEntities()
        } catch (error) {
            window.error(error)
        } finally {
            show = false
        }
    }

    async function saveEntity() {
        if (entity.paymentLinkId) {
            updateEntity()
        } else {
            createEntity()
        }
    }

    async function loadEntities() {
        try {
            show = true
            step = 1
            entities = await window.api.payments.paymentLinks.getAll({ category: 'subscription' })
            loadGrid()
        } catch (error) {
            window.error(error)
        } finally {
            show = false
        }
    }

    async function removeEntity() {
        if (!confirm('Deseja mesmo excluir este registro?')) {
            return
        }
        try {
            show = true
            await window.api.payments.paymentLinks.remove(entity.paymentLinkId)
            await loadEntities()
        } catch (error) {
            window.error(error)
        } finally {
            show = false
        }
    }

    function showEntities() {
        step = 1
    }

    let show = false
    let step = 1
    let entities: PaymentLink[] = []

    let gateways: Gateway[] = []
    let gateway: Gateway = null
    async function loadGateways() {
        gateways = await window.api.payments.gateways.getAll()
        if (gateways.length === 1) {
            gateway = gateways[0]
        }
    }

    onMount(async () => {
        await loadGateways()
        await loadEntities()
        if (form) {
            if (paymentLinkId) {
                await editEntity(paymentLinkId)
            } else {
                newEntity()
            }
        }
    })

    // rótulo único, no enum: as três telas diziam a mesma coisa com palavras diferentes
    function friendlyType(type: PurchaseType) {
        return PurchaseTypeFriendly[type] || type
    }

    let grid: GridClass = new GridClass()
    function loadGrid() {
        // grid
        grid = new GridClass()
        // grid head
        grid.title = 'Assinaturas'
        grid.subtitle = ''
        grid.showActs = false
        grid.cols.push(new GridCol({ content: 'Descrição', type: GridColTypeEnum.string }))
        grid.cols.push(new GridCol({ content: 'Método', type: GridColTypeEnum.string }))
        grid.cols.push(new GridCol({ content: 'Parcelamento', type: GridColTypeEnum.string }))
        grid.cols.push(new GridCol({ content: 'Valor', type: GridColTypeEnum.float_2 }))
        grid.cols.push(new GridCol({ content: '', type: GridColTypeEnum.button }))
        // grid lines
        for (const item of entities) {
            const btnEdit = new GridButton({
                callback: () => editEntity(item.paymentLinkId),
                icon: faEdit,
                label: 'Editar',
            })
            let line: GridLine = new GridLine()
            line.slots.push({ content: item.purchaseDescription })
            line.slots.push({ content: friendlyType(item.purchaseType) })
            let type = '-'
            if (item.purchaseType !== PurchaseType.recurring_continuous) {
                type = `até ${item.purchaseTotalInstallments}x`
            }
            line.slots.push({ content: type })
            line.slots.push({ content: item.purchaseTotalValue })
            if (window.store.token.permissions.pay.links.write) {
                line.slots.push({ content: [btnEdit] })
            } else {
                line.slots.push({ content: [] })
            }

            grid.lines.push(line)
        }
    }

    // Tipos aceitos pela combinação gateway + meio de pagamento escolhidos. Vem da api: o Mercado
    // Pago, por exemplo, só faz cobrança convencional, então recorrente nem aparece como opção.
    // Sem meio definido, mostra a união do que o gateway aceita em algum meio.
    //
    // `list` é parâmetro (e não a variável de cima) para que o cálculo reativo enxergue os gateways
    // como dependência — o Svelte não olha dentro da função para montar a lista de dependências.
    function purchaseTypeOptions(gatewayId: string, method: string, list: Gateway[]) {
        const found = list.find((el) => el.gatewayId === gatewayId)
        const mapa = found?.gatewayPurchaseTypes || {}
        const tipos = method ? mapa[method] || [] : [...new Set(Object.values(mapa).flat())]
        return tipos.map((tipo) => ({ value: tipo, label: PurchaseTypeFriendly[tipo] || tipo }))
    }

    // Meios aceitos pelo gateway escolhido, já com o rótulo de exibição. Vem da api — antes as
    // opções eram fixas no template, então um meio novo (pix, por exemplo) não aparecia mesmo com
    // o gateway aceitando, e sem erro nenhum para denunciar.
    function paymentMethodOptions(gatewayId: string, list: Gateway[]) {
        const found = list.find((el) => el.gatewayId === gatewayId)
        return (found?.gatewayPaymentMethods || []).map((method) => ({
            value: method,
            label: PaymentMethodsFriendly[method] || method,
        }))
    }

    // opção vazia explícita: sem ela o select fica sem seleção visível enquanto o valor está vazio
    $: gatewayOptions = [{ value: null, label: '' }, ...gateways.map((item) => ({ value: item.gatewayId, label: item.label }))]
    // Um meio só serve nesta tela se aceitar assinatura NESTE gateway. Sem o filtro, o cartão do
    // Mercado Pago aparecia aqui e, ao ser escolhido, deixava 'Método de cobrança' vazio — o meio
    // existe no gateway, mas não para recorrência: o token de cartão de lá é de uso único e cada
    // ciclo exigiria o portador na tela de novo.
    //
    // A fonte é a mesma que já monta tipoOptions: gatewayPurchaseTypes, que a api publica por
    // gateway e por meio. Nada aqui é regra de negócio local.
    function aceitaAssinatura(gatewayId: string, method: string, list: Gateway[]) {
        return purchaseTypeOptions(gatewayId, method, list).some((t) => isSubscriptionType(t.value))
    }

    // O meio já gravado permanece na lista mesmo que não passe no filtro: esta tela também edita
    // assinaturas antigas, e sumir com a opção trocaria em silêncio o que está salvo.
    $: meioOptions = [
        { value: '', label: '' },
        ...paymentMethodOptions(entity.gatewayId, gateways).filter(
            (m) => m.value === entity.paymentMethodsAllowed || aceitaAssinatura(entity.gatewayId, m.value, gateways),
        ),
    ]

    // esta tela é de assinaturas, então só os tipos recorrentes que o gateway aceita:
    // no Mercado Pago a lista fica vazia de propósito
    // Assinatura é só a recorrente contínua. O carnê (recorrente fixa) divide um valor total em
    // vencimentos e não tem cancelamento nem pro-rata — é venda fracionada, e mora no cadastro de
    // cobranças. A fronteira vive em isSubscriptionType().
    $: tipoOptions = purchaseTypeOptions(entity.gatewayId, entity.paymentMethodsAllowed, gateways).filter((t) => isSubscriptionType(t.value))

    $: parcelamentoAberto =
        (entity.purchaseType === PurchaseType.recurring_fixed || entity.purchaseType === PurchaseType.full_charge) &&
        entity.paymentMethodsAllowed !== PaymentMethods.bankslip

    $: temIntervalo = entity.purchaseType === PurchaseType.recurring_fixed || entity.purchaseType === PurchaseType.recurring_continuous

    const intervaloOptions = [{ value: 1, label: '1 mês' }, ...Array.from({ length: 11 }, (_, index) => ({ value: index + 2, label: index + 2 + ' meses' }))]

    const lancamentoOptions = [
        { value: 'start', label: 'Apenas uma vez ao pagar a primeira parcela' },
        { value: 'every', label: 'Sempre que pagar uma parcela' },
    ]
</script>

{#if step === 1}
    <Content footer={true}>
        <Zact>
            <div class="spacer" />
            {#if window.store.token.permissions.pay.links.write}
                <Zbutton color="primary" icon={faPlusCircle} label="Cadastrar nova assinatura" mobileHideLabel={true} on:click={newEntity} />
            {/if}
        </Zact>
        <Zgrid class="h-space" {grid} />
        <LoadSpinner {show} />
    </Content>
{/if}
{#if step === 2}
    <Content footer={true}>
        <Zact>
            {#if !form}
                <Zbutton color="secondary" icon={faAngleLeft} label="Voltar" on:click={showEntities} />
            {/if}
            <div class="spacer" />
            {#if window.store.token.permissions.pay.links.write}
                {#if !!entity.paymentLinkId}
                    <Zbutton color="red" icon={faTrashAlt} label="Excluir" disabled={show} on:click={removeEntity} />
                {/if}
                <Zbutton color="primary" icon={faSave} label="Salvar" loading={show} disabled={show} on:click={saveEntity} />
            {/if}
        </Zact>
        <Zframe title="Assinatura" class="h-space">
            <ZContainer gapRow="8px" gapCol="8px">
                <Zinput size={6} sizeM={12} label="Nome da assinatura" type="text" bind:value={entity.purchaseDescription} />
                <Zinput size={6} sizeM={12} label={purchaseTotalLabel} type="number" bind:value={entity.purchaseTotalValue} />
                <ZinputTitle title="Gateway"></ZinputTitle>
                <Zinput size={4} sizeM={12} label="Gateway de pagamento padrão" type="select" options={gatewayOptions} bind:value={entity.gatewayId} />
                <Zinput size={4} sizeM={12} label="Meio de pagamento" type="select" options={meioOptions} bind:value={entity.paymentMethodsAllowed} />
                <Zinput size={4} sizeM={12} label="Método de cobrança" type="select" options={tipoOptions} bind:value={entity.purchaseType} />
                {#if parcelamentoAberto}
                    <Zinput size={4} sizeM={12} label="Parcelamento máximo" type="number" bind:value={entity.purchaseTotalInstallments} />
                {:else}
                    <Zinput size={4} sizeM={12} label="Parcelamento máximo" type="number" value={1} disabled={true} />
                {/if}
                {#if temIntervalo}
                    <Zinput
                        size={4}
                        sizeM={12}
                        label="Intervalo entre parcelas"
                        type="select"
                        options={intervaloOptions}
                        bind:value={entity.purchaseInterval}
                    />
                {/if}
                {#if entity.purchaseType === PurchaseType.recurring_continuous}
                    <Zinput
                        size={4}
                        sizeM={12}
                        label="Cancelar com quantos dias de atraso"
                        type="number"
                        help="Ao chegar neste atraso, o cancelamento é agendado para 7 dias depois — tempo para o cliente pagar ou para você desfazer o agendamento na assinatura. Se a dívida for paga antes, o agendamento é desfeito sozinho. Vale para as assinaturas contratadas a partir de agora. Deixe vazio para nunca cancelar."
                        bind:value={entity.autoCancelOverdueDays}
                    />
                {/if}
                <ZinputBreak></ZinputBreak>
                <ZinputTitle title="Dados da compra"></ZinputTitle>
                <ZContainerCol size={4} sizeM={12}>
                    <ZentitySelect fonte="servico" label="Item adquirido" params={{ type: 'all', price: true }} bind:value={selectedService} />
                </ZContainerCol>
                <ZContainerCol size={4} sizeM={12}>
                    <ZentitySelect fonte="usuario" label="Vendedor" bind:value={selectedUser} />
                </ZContainerCol>
                <ZContainerCol size={4} sizeM={12}>
                    <ZentitySelect fonte="meioPagamento" label="Meio de pagamento" bind:value={selectedPayment} />
                </ZContainerCol>
                <Zinput
                    size={8}
                    sizeM={12}
                    label="Lançar no PDV e saldo do cliente"
                    type="select"
                    options={lancamentoOptions}
                    bind:value={entity.purchaseLaunchType}
                />
            </ZContainer>
        </Zframe>
        <LoadSpinner {show} />
    </Content>
{/if}
